import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronRight, Plus, Pencil, Trash2, Users, Upload, SlidersHorizontal, CheckSquare, Square, MinusSquare, Tag, Download } from 'lucide-react';
import { api } from '../lib/api.ts';
import {
  extractError,
  type PagedResponse,
  type CustomerListItem,
  customerFullName,
} from '../lib/types.ts';
import { formatDate } from '../lib/utils.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { CustomerStatusBadge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { Pagination, usePagination } from '../components/ui/Pagination.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { CustomerImportWizard } from '../components/customers/CustomerImportWizard.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

// CustomerListItem is imported from lib/types.ts — no local re-definition needed.
// Use an alias so the rest of the file can keep the short name 'Customer'.
type Customer = CustomerListItem;

interface CustomerForm {
  firstName: string;
  lastName: string;
  externalId: string;
  email: string;
  phone: string;
  status: string;
}

const EMPTY_FORM: CustomerForm = {
  firstName: '',
  lastName: '',
  externalId: '',
  email: '',
  phone: '',
  status: 'active',
};

function fullName(c: Customer): string {
  return customerFullName(c);
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CustomersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const pag = usePagination(50);

  const [page, setPage] = useState(1);
  // Reset selection when page changes so selectedIds always matches visible rows
  const setPageAndClearSelection = (p: number) => { setPage(p); setSelectedIds(new Set()); };
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // uncontrolled display value
  const [statusFilter, setStatusFilter] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [acquisitionSourceFilter, setAcquisitionSourceFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [attrKey, setAttrKey] = useState('');
  const [attrValue, setAttrValue] = useState('');

  // bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [bulkTag, setBulkTag] = useState('');
  const [showBulkStatus, setShowBulkStatus] = useState(false);

  // modals
  const [showCreate, setShowCreate] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);

  const activeFilterCount = [countryFilter, languageFilter, acquisitionSourceFilter, createdFrom, createdTo, tagFilter, attrKey].filter(Boolean).length;

  // Close bulk status dropdown on outside click
  const bulkStatusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showBulkStatus) return;
    function handleOutside(e: MouseEvent) {
      if (bulkStatusRef.current && !bulkStatusRef.current.contains(e.target as Node)) {
        setShowBulkStatus(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showBulkStatus]);

  const { data, isLoading, isError } = useQuery<PagedResponse<Customer>>({
    queryKey: ['customers', page, search, statusFilter, countryFilter, languageFilter, acquisitionSourceFilter, createdFrom, createdTo, tagFilter, attrKey, attrValue],
    queryFn: () =>
      api
        .get('/api/v1/customers', {
          params: {
            page,
            limit: pag.limit,
            ...(search ? { search } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(countryFilter ? { country: countryFilter } : {}),
            ...(languageFilter ? { language: languageFilter } : {}),
            ...(acquisitionSourceFilter ? { acquisitionSource: acquisitionSourceFilter } : {}),
            ...(createdFrom ? { createdFrom } : {}),
            ...(createdTo ? { createdTo } : {}),
            ...(tagFilter ? { tags: tagFilter } : {}),
            ...(attrKey && attrValue ? { attributeKey: attrKey, attributeValue: attrValue } : {}),
          },
        })
        .then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const customers: Customer[] = data?.items ?? [];
  const totalPages = pag.totalPages(data?.total ?? 0);

  // ── mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (body: CustomerForm) =>
      api
        .post('/api/v1/customers', {
          firstName: body.firstName || undefined,
          lastName: body.lastName || undefined,
          externalId: body.externalId || undefined,
          email: body.email || undefined,
          phone: body.phone || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success('Customer created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create customer')),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<CustomerForm> }) => {
      await api.patch(`/api/v1/customers/${id}`, {
        firstName: body.firstName || undefined,
        lastName: body.lastName || undefined,
        status: body.status,
      });
      // Only call contacts endpoint when at least one value is non-empty.
      // Clearing email/phone requires the Contacts section on the customer detail page
      // (UpsertContactSchema does not support null — backend would need changes to support clearing).
      const hasContactData = !!(body.email || body.phone);
      if (hasContactData) {
        await api.put(`/api/v1/customers/${id}/contacts`, {
          email: body.email || undefined,
          phone: body.phone || undefined,
          isPrimary: true,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setEditCustomer(null);
      toast.success('Customer updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update customer')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/customers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setDeleteTarget(null);
      toast.success('Customer deleted (GDPR anonymised)');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to delete customer')),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const ids = [...selectedIds];
      const results = await Promise.allSettled(
        ids.map((id) => api.patch(`/api/v1/customers/${id}`, { status }))
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) throw new Error(`${failures.length} of ${ids.length} updates failed`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setSelectedIds(new Set());
      setShowBulkStatus(false);
      toast.success('Status updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update status')),
  });

  const bulkTagMutation = useMutation({
    mutationFn: async (tag: string) => {
      const ids = [...selectedIds];
      const results = await Promise.allSettled(
        ids.map((id) => api.post(`/api/v1/customers/${id}/tags/${encodeURIComponent(tag)}`))
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) throw new Error(`${failures.length} of ${ids.length} tag additions failed`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setSelectedIds(new Set());
      setShowBulkTag(false);
      setBulkTag('');
      toast.success('Tag added');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to add tag')),
  });

  // ── helpers ──────────────────────────────────────────────────────────────────

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  function openEdit(c: Customer) {
    setForm({
      firstName: c.firstName ?? '',
      lastName: c.lastName ?? '',
      externalId: c.externalId ?? '',
      email: c.email ?? '',
      phone: c.phone ?? '',
      status: c.status,
    });
    setEditCustomer(c);
  }

  function handleSearch(val: string) {
    setSearchInput(val);
    setSelectedIds(new Set());
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  }

  function handleStatusFilter(val: string) {
    setStatusFilter(val);
    setSelectedIds(new Set());
    setPage(1);
  }

  function clearAllFilters() {
    handleSearch('');
    setStatusFilter('');
    setCountryFilter('');
    setLanguageFilter('');
    setAcquisitionSourceFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setTagFilter('');
    setAttrKey('');
    setAttrValue('');
    setSelectedIds(new Set());
    setPage(1);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (customers.every((c) => selectedIds.has(c.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(customers.map((c) => c.id)));
    }
  }

  function exportCsv() {
    const selected = customers.filter((c) => selectedIds.has(c.id));
    const rows = [
      ['ID', 'Nome', 'Email', 'Telefono', 'Status', 'Creato'],
      ...selected.map((c) => [
        c.id,
        `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
        c.email ?? '',
        c.phone ?? '',
        c.status,
        formatDate(c.createdAt),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <PageHeader
        title="Customers"
        description="All customers registered in this tenant"
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowImport(true)}>
              <Upload size={13} className="mr-1" /> Import CSV
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus size={13} className="mr-1" /> New customer
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {/* Search */}
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search name, email, phone, company…"
            className="h-9 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 w-72"
          />
          {searchInput && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="h-9 pl-3 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 appearance-none min-w-[140px]"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="blocked">Blocked</option>
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">
            ▾
          </span>
        </div>

        {/* Advanced filters toggle */}
        <button
          onClick={() => setShowAdvancedFilters((v) => !v)}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm transition-colors ${
            showAdvancedFilters || activeFilterCount > 0
              ? 'bg-zinc-900 text-white border-zinc-900'
              : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-0.5 bg-white text-zinc-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>

        {(searchInput || statusFilter || activeFilterCount > 0) && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            Clear all
          </button>
        )}

        <span className="ml-auto text-xs text-zinc-400 tabular-nums">{data?.total ?? 0} total</span>
      </div>

      {/* Advanced filters panel */}
      {showAdvancedFilters && (
        <div className="mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Country (ISO)</label>
            <input
              type="text"
              value={countryFilter}
              onChange={(e) => { setCountryFilter(e.target.value.toUpperCase()); setPage(1); }}
              placeholder="IT"
              maxLength={2}
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Language</label>
            <input
              type="text"
              value={languageFilter}
              onChange={(e) => { setLanguageFilter(e.target.value); setPage(1); }}
              placeholder="it, en, fr…"
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Acquisition source</label>
            <input
              type="text"
              value={acquisitionSourceFilter}
              onChange={(e) => { setAcquisitionSourceFilter(e.target.value); setPage(1); }}
              placeholder="newsletter, ads…"
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Tag</label>
            <input
              type="text"
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value.toLowerCase()); setPage(1); }}
              placeholder="vip, newsletter…"
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Created from</label>
            <input
              type="date"
              value={createdFrom}
              onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }}
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Created to</label>
            <input
              type="date"
              value={createdTo}
              onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }}
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Attribute key</label>
            <input
              type="text"
              value={attrKey}
              onChange={(e) => { setAttrKey(e.target.value); setPage(1); }}
              placeholder="membership_level"
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Attribute value</label>
            <input
              type="text"
              value={attrValue}
              onChange={(e) => { setAttrValue(e.target.value); setPage(1); }}
              placeholder="gold"
              className="h-8 px-2 w-full bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="pl-4 pr-2 py-3 w-8">
                <button
                  onClick={toggleSelectAll}
                  className="text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  {customers.length > 0 && customers.every((c) => selectedIds.has(c.id)) ? (
                    <CheckSquare size={15} />
                  ) : customers.some((c) => selectedIds.has(c.id)) ? (
                    <MinusSquare size={15} />
                  ) : (
                    <Square size={15} />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                External ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={8} cols={7} />
            ) : isError ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Users size={26} />}
                    title="Failed to load customers"
                    description="An error occurred while fetching the customer list. Please try again."
                  />
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Users size={26} />}
                    title={search || statusFilter ? 'No customers match' : 'No customers yet'}
                    description={
                      search || statusFilter
                        ? 'Try different search terms.'
                        : 'Create a customer or wait for API ingestion.'
                    }
                    action={
                      !search && !statusFilter
                        ? { label: '+ New customer', onClick: openCreate }
                        : undefined
                    }
                  />
                </td>
              </tr>
            ) : (
              customers.map((c) => (
                <tr
                  key={c.id}
                  className={`hover:bg-zinc-50/70 transition-colors group ${selectedIds.has(c.id) ? 'bg-zinc-50' : ''}`}
                >
                  <td className="pl-4 pr-2 py-3 w-8">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(c.id); }}
                      className="text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      {selectedIds.has(c.id) ? <CheckSquare size={15} className="text-zinc-700" /> : <Square size={15} />}
                    </button>
                  </td>
                  <td
                    className="px-4 py-3 font-medium text-zinc-900 cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    {fullName(c)}
                  </td>
                  <td
                    className="px-4 py-3 text-zinc-500 text-sm cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    {c.email ?? <span className="text-zinc-300">—</span>}
                  </td>
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <code className="text-xs text-zinc-500 font-mono">{c.externalId ?? '—'}</code>
                  </td>
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <CustomerStatusBadge status={c.status} />
                  </td>
                  <td
                    className="px-4 py-3 text-zinc-500 text-xs cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    {formatDate(c.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(c);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(c);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                      <ChevronRight
                        size={14}
                        className="text-zinc-300 group-hover:text-zinc-500 transition-colors ml-1 cursor-pointer"
                        onClick={() => navigate(`/customers/${c.id}`)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="border-t border-zinc-100 px-4">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPageAndClearSelection} />
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Customer"
        description="Create a new customer profile in this tenant."
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First name"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              placeholder="Mario"
            />
            <Input
              label="Last name"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              placeholder="Rossi"
            />
          </div>
          <Input
            label="External ID"
            hint="Your system's identifier for this customer"
            value={form.externalId}
            onChange={(e) => setForm((f) => ({ ...f, externalId: e.target.value }))}
            placeholder="CRM-12345"
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="mario@example.com"
          />
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+39 02 1234567"
          />
          {createMutation.isError && (
            <p className="text-xs text-red-500">{extractError(createMutation.error)}</p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={createMutation.isPending}
              disabled={!form.firstName && !form.lastName && !form.externalId && !form.email}
              onClick={() => createMutation.mutate(form)}
            >
              Create customer
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editCustomer}
        onClose={() => setEditCustomer(null)}
        title="Edit Customer"
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First name"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            />
            <Input
              label="Last name"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+39 02 1234567"
          />
          <Select
            label="Status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="blocked">Blocked</option>
          </Select>
          {updateMutation.isError && (
            <p className="text-xs text-red-500">{extractError(updateMutation.error)}</p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setEditCustomer(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={updateMutation.isPending}
              onClick={() =>
                editCustomer && updateMutation.mutate({ id: editCustomer.id, body: form })
              }
            >
              Save changes
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete customer?"
        message={`This will permanently anonymise "${deleteTarget ? fullName(deleteTarget) : ''}" (GDPR). This action cannot be undone.`}
        confirmLabel="Delete & anonymise"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* CSV Import Wizard */}
      <CustomerImportWizard open={showImport} onClose={() => setShowImport(false)} />

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-zinc-900 text-white px-5 py-3 rounded-2xl shadow-xl text-sm">
          <span className="font-medium tabular-nums">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-zinc-600" />
          <div className="relative" ref={bulkStatusRef}>
            <button
              onClick={() => { setShowBulkStatus((v) => !v); setShowBulkTag(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors text-xs"
            >
              Change status
            </button>
            {showBulkStatus && (
              <div className="absolute bottom-full mb-2 left-0 bg-white text-zinc-800 rounded-xl shadow-lg border border-zinc-200 overflow-hidden text-xs min-w-[130px]">
                {(['active', 'inactive', 'blocked'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => bulkStatusMutation.mutate(s)}
                    disabled={bulkStatusMutation.isPending}
                    className="w-full text-left px-4 py-2.5 hover:bg-zinc-50 capitalize transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => { setShowBulkTag((v) => !v); setShowBulkStatus(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors text-xs"
          >
            <Tag size={12} /> Add tag
          </button>
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors text-xs"
          >
            <Download size={12} /> Export CSV
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-1 text-zinc-400 hover:text-white transition-colors"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Bulk tag modal */}
      <Modal
        open={showBulkTag}
        onClose={() => { setShowBulkTag(false); setBulkTag(''); }}
        title="Add tag to selected"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Tag"
            value={bulkTag}
            onChange={(e) => setBulkTag(e.target.value.toLowerCase())}
            placeholder="vip, newsletter…"
            onKeyDown={(e) => e.key === 'Enter' && bulkTag.trim() && bulkTagMutation.mutate(bulkTag.trim())}
          />
          {bulkTagMutation.isError && (
            <p className="text-xs text-red-500">{extractError(bulkTagMutation.error)}</p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => { setShowBulkTag(false); setBulkTag(''); }}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={bulkTagMutation.isPending}
              disabled={!bulkTag.trim()}
              onClick={() => bulkTagMutation.mutate(bulkTag.trim())}
            >
              Add tag
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}
